import {useState} from 'react'
import * as Yup from 'yup'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {Mail, AlertCircle, CheckCircle2} from 'lucide-react'
import {passwordRecovery} from '../core/_requests'
import {TOKENS} from '../../../components/design-system'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email inválido')
    .min(3, 'Mínimo 3 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('Email é obrigatório'),
})

const FieldError = ({ message }: { message?: string }) => {
  if (!message) return null
  return (
    <div style={{ fontSize: 11, color: TOKENS.color.severity.high.text, marginTop: 4 }}>
      {message}
    </div>
  )
}

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        passwordRecovery(values.email)
          .then(() => {
            setHasErrors(false)
            setLoading(false)
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus('Login incorreto')
          })
      }, 1000)
    },
  })

  const baseInput: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px 10px 38px',
    fontSize: 14,
    border: `1px solid ${TOKENS.color.border}`,
    borderRadius: TOKENS.radius.card,
    background: TOKENS.color.card,
    color: TOKENS.color.text,
    outline: 'none',
  }

  return (
    <form
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}
    >
      <div style={{ textAlign: 'center', marginBottom: TOKENS.spacing.lg }}>
        <div style={{
          fontSize: TOKENS.typography.label.size,
          fontWeight: TOKENS.typography.label.weight,
          textTransform: TOKENS.typography.label.transform,
          letterSpacing: TOKENS.typography.label.tracking,
          color: TOKENS.color.muted,
          marginBottom: 6,
        }}>
          Recuperação
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: TOKENS.color.text }}>
          Esqueceu a senha?
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: TOKENS.color.muted }}>
          Insira o seu e-mail para redefinir a senha.
        </div>
      </div>

      {hasErrors === true && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm,
          background: TOKENS.color.severity.high.bg,
          color: TOKENS.color.severity.high.text,
          border: `1px solid ${TOKENS.color.severity.high.text}33`,
          borderRadius: TOKENS.radius.card,
          padding: '10px 12px', fontSize: 13, fontWeight: 600,
        }}>
          <AlertCircle size={16} />
          Desculpe, parece que alguns erros foram detectados, tente novamente.
        </div>
      )}

      {hasErrors === false && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm,
          background: TOKENS.color.severity.low.bg,
          color: TOKENS.color.severity.low.text,
          border: `1px solid ${TOKENS.color.severity.low.text}33`,
          borderRadius: TOKENS.radius.card,
          padding: '10px 12px', fontSize: 13, fontWeight: 600,
        }}>
          <CheckCircle2 size={16} />
          Redefinição de senha enviada. Verifique seu email.
        </div>
      )}

      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: TOKENS.color.muted, display: 'block', marginBottom: 6 }}>Email</label>
        <div style={{ position: 'relative' }}>
          <Mail size={16} color={TOKENS.color.muted} style={{ position: 'absolute', left: 12, top: 13, pointerEvents: 'none' }} />
          <input
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            style={baseInput}
          />
        </div>
        <FieldError message={formik.touched.email && formik.errors.email ? formik.errors.email : undefined} />
      </div>

      <div style={{ display: 'flex', gap: TOKENS.spacing.sm, marginTop: TOKENS.spacing.sm }}>
        <button
          type='submit'
          id='kt_password_reset_submit'
          disabled={formik.isSubmitting || !formik.isValid}
          style={{
            flex: 1, border: 'none',
            background: (formik.isSubmitting || !formik.isValid) ? TOKENS.color.border : TOKENS.color.action,
            color: (formik.isSubmitting || !formik.isValid) ? TOKENS.color.muted : '#fff',
            padding: '12px 16px', borderRadius: TOKENS.radius.card,
            fontSize: 14, fontWeight: 700, letterSpacing: 0.3,
            cursor: (formik.isSubmitting || !formik.isValid) ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Aguarde…' : 'Enviar'}
        </button>
        <Link
          to='/auth/login'
          style={{
            flex: 1, border: `1px solid ${TOKENS.color.border}`,
            background: TOKENS.color.card, color: TOKENS.color.text,
            padding: '12px 16px', borderRadius: TOKENS.radius.card,
            fontSize: 14, fontWeight: 600, textAlign: 'center', textDecoration: 'none',
          }}
        >
          Cancelar
        </Link>
      </div>
    </form>
  )
}
